/**
 * InstallStepList (Plan 06-03, Task 1).
 *
 * The 4-step iOS install walkthrough body. Renders an <ol> with an
 * `aria-label` from `t('install.steps_aria')` ("Installasjonssteg") and
 * four <li> rows, each pairing a 24px icon decoration with a numbered
 * heading + body in Norwegian Bokmål.
 *
 * Icon map (per 06-UI-SPEC §"Layout Shapes" 3):
 *   step1 — Icon.Share   (iOS share-sheet arrow-up)
 *   step2 — Icon.Plus    ("Legg til på hjemmskjermen" plus glyph)
 *   step3 — Icon.Bell    (notification permission prompt)
 *   step4 — Icon.Check   (done!)
 *
 * Typography:
 *   - Number+heading line: text-sm font-semibold text-ink
 *   - Body: text-sm text-ink-2
 *
 * Layout (per UI-SPEC §"Layout Shapes" 3):
 *   - Outer <ol> mt-8 space-y-5
 *   - Each <li> flex items-start gap-3
 *   - Icon block 24×24 in text-ink-2
 *   - Step body column flex flex-col gap-0.5
 *
 * Reduced motion: nothing animates (per UI-SPEC D-07).
 *
 * i18n keys (from 06-01 Task 4):
 *   install.steps_aria
 *   install.step{1..4}.{label,heading,body}
 */
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/primitives';

interface StepRow {
  index: 1 | 2 | 3 | 4;
  IconCmp: typeof Icon.Share | typeof Icon.Plus | typeof Icon.Bell | typeof Icon.Check;
}

const STEPS: ReadonlyArray<StepRow> = [
  { index: 1, IconCmp: Icon.Share },
  { index: 2, IconCmp: Icon.Plus },
  { index: 3, IconCmp: Icon.Bell },
  { index: 4, IconCmp: Icon.Check },
];

export function InstallStepList(): JSX.Element {
  const { t } = useTranslation();
  return (
    <ol aria-label={t('install.steps_aria')} className="mt-8 space-y-5">
      {STEPS.map(({ index, IconCmp }) => (
        <li key={index} className="flex items-start gap-3">
          <IconCmp size={24} className="text-ink-2 shrink-0 mt-0.5" />
          <div className="flex flex-col gap-0.5">
            <p className="text-sm font-semibold text-ink">
              {t(`install.step${index}.label`)} {t(`install.step${index}.heading`)}
            </p>
            <p className="text-sm text-ink-2">
              {t(`install.step${index}.body`)}
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
}
